import { MoreVertical, Plus, Trash } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { IconButton } from "src/components/ui/icon-button";
import BitbucketIcon from "src/components/ui/icons/bitbucket";
import { ErrorAlert } from "src/components/ui/status-alert";
import { Tooltip } from "src/components/ui/tooltip";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { BitbucketIntegrationsDialogDeleteIntegrationMutation } from "./__generated__/BitbucketIntegrationsDialogDeleteIntegrationMutation.graphql";
import type { BitbucketIntegrationsDialogQuery } from "./__generated__/BitbucketIntegrationsDialogQuery.graphql";
import BitbucketIntegration from "./BitbucketIntegration";

interface BitbucketIntegrationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const bitbucketIntegrationsDialogQuery = graphql`
  query BitbucketIntegrationsDialogQuery {
    me {
      ...appMe
      organization {
        integrations(integrationType: BITBUCKET) {
          edges {
            node {
              id
              name
              integrationType
              integrationStatus
              createdAt
              bitbucketUsername
              bitbucketWorkspaceSlugs
            }
          }
        }
      }
    }
  }
`;

const deleteIntegrationMutation = graphql`
  mutation BitbucketIntegrationsDialogDeleteIntegrationMutation($input: DeleteIntegrationInput!) {
    deleteIntegration(input: $input) {
      success
      error
    }
  }
`;

export default function BitbucketIntegrationsDialog({ isOpen, onClose, onRefetch, brandTheme }: BitbucketIntegrationsDialogProps) {
  const [isConnectingNew, setIsConnectingNew] = useState(false);
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    isOpen: boolean;
    integration: any | null;
  }>({
    isOpen: false,
    integration: null,
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { toast } = useToast();

  const [commitDeleteIntegration] = useMutation<BitbucketIntegrationsDialogDeleteIntegrationMutation>(
    deleteIntegrationMutation
  );

  const data = useLazyLoadQuery<BitbucketIntegrationsDialogQuery>(
    bitbucketIntegrationsDialogQuery,
    {},
    { fetchPolicy: isOpen ? "network-only" : "store-only" },
  );

  const bitbucketIntegrations = data?.me.organization?.integrations?.edges || [];

  const handleConnectAnother = () => {
    setIsConnectingNew(true);
  };

  const handleDeleteClick = (integration: any) => {
    setDeleteConfirmDialog({
      isOpen: true,
      integration,
    });
    setDeleteError(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmDialog.integration) {
      setDeleteError("Integration not found");
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    commitDeleteIntegration({
      variables: {
        input: {
          id: deleteConfirmDialog.integration.id,
        },
      },
      onCompleted: (response, errors) => {
        setIsDeleting(false);

        if (errors && errors.length > 0) {
          setDeleteError(errors.map(e => e.message).join(", "));
          return;
        }

        if (response.deleteIntegration.error) {
          setDeleteError(response.deleteIntegration.error);
          return;
        }

        if (response.deleteIntegration.success) {
          setDeleteConfirmDialog({ isOpen: false, integration: null });
          toast({
            title: "Bitbucket integration disconnected",
            variant: "success",
          });

          if (bitbucketIntegrations.length <= 1) {
            onClose();
          }

          onRefetch?.();
        }
      },
      onError: (error) => {
        setIsDeleting(false);
        setDeleteError(error.message);
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({ isOpen: false, integration: null });
    setDeleteError(null);
  };

  // If connecting new, show the connection flow
  if (isConnectingNew) {
    return (
      <BitbucketIntegration
        isOpen={isOpen}
        onClose={() => {
          setIsConnectingNew(false);
          onClose();
        }}
        brandTheme={brandTheme}
      />
    );
  }

  // If no integrations, show the full Bitbucket connection flow
  if (bitbucketIntegrations.length === 0) {
    return <BitbucketIntegration isOpen={isOpen} onClose={onClose} brandTheme={brandTheme} />;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <BitbucketIcon className="h-6 w-6" />
            Bitbucket Integrations
          </DialogTitle>
          <DialogDescription>
            Manage your Bitbucket integrations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-foreground">Connected Integrations</h3>

            {/* Header row */}
            <div className="grid grid-cols-[minmax(120px,1fr)_minmax(200px,2fr)_minmax(120px,1fr)_40px] gap-4 pb-2 border-b border-accent">
              <div className="text-sm font-medium text-muted-foreground">Username</div>
              <div className="text-sm font-medium text-muted-foreground">Workspaces</div>
              <div className="text-sm font-medium text-muted-foreground">Connected</div>
              <div></div>
            </div>

            {/* Integration rows */}
            {bitbucketIntegrations.map((edge): React.ReactNode => {
              const integration = edge.node;
              const username = integration.bitbucketUsername || "Unknown";
              const workspaces = integration.bitbucketWorkspaceSlugs || [];
              const createdAt = integration.createdAt ? new Date(integration.createdAt).toLocaleDateString() : "N/A";

              return (
                <div key={integration.id} className="grid grid-cols-[minmax(120px,1fr)_minmax(200px,2fr)_minmax(120px,1fr)_40px] gap-4 items-start py-2">
                  <div className="text-foreground font-medium truncate">{username}</div>
                  <div className="text-muted-foreground text-sm pt-0.5 min-w-0 truncate">
                    {workspaces.length > 0 ? workspaces.join(", ") : "No workspaces"}
                  </div>
                  <div className="text-muted-foreground text-sm pt-0.5 min-w-0">{createdAt}</div>
                  <div className="flex gap-2 items-start pt-0.5 justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <IconButton
                          icon={MoreVertical}
                          variant="none"
                          size="sm"
                          className="flex-shrink-0 h-6 w-6 flex items-center justify-center cursor-pointer text-muted-foreground bg-accent hover:bg-cyan hover:text-primary-foreground rounded-md"
                          title="More options"
                        />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-card border-border text-foreground">
                        <div>
                          <Tooltip
                            content={!canModify ? "You don't have permission to disconnect integrations" : null}
                            position="left"
                          >
                            <DropdownMenuItem
                              onClick={() => canModify && handleDeleteClick(integration)}
                              className="hover:bg-accent cursor-pointer text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={!canModify}
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              Disconnect
                            </DropdownMenuItem>
                          </Tooltip>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}

            {/* Action buttons */}
            <div className="pt-4 border-t border-accent">
              <p className="text-sm text-muted-foreground mb-3">
                Connect {productName} to additional Bitbucket accounts for repository access and analysis.
              </p>
              <Tooltip
                content={!canModify ? "You don't have permission to connect integrations" : null}
                position="top"
              >
                <span className="inline-block">
                  <Button
                    onClick={handleConnectAnother}
                    disabled={!canModify}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Connect Another Bitbucket
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Delete confirmation dialog */}
      <UIConfirmationDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Disconnect Bitbucket Integration"
        confirmText={isDeleting ? "Disconnecting..." : "Disconnect"}
        cancelText="Cancel"
        variant="destructive"
        confirmDisabled={isDeleting}
      >
        <div className="space-y-3">
          <p>
            Are you sure you want to disconnect the Bitbucket integration for{" "}
            <span className="font-semibold">
              {deleteConfirmDialog.integration?.bitbucketUsername || "this account"}
            </span>
            ?
          </p>
          <p className="text-sm text-muted-foreground">
            This will remove access to all repositories from this integration. This action cannot be undone.
          </p>

          {deleteError && (
            <ErrorAlert icon="none">
              {deleteError}
            </ErrorAlert>
          )}
        </div>
      </UIConfirmationDialog>
    </Dialog>
  );
}
